import {
  defaultPagination,
  useDate,
  useNotification,
  useServiceHandlers,
} from '@/core'
import type {
  BaseServiceOptions,
  Pagination,
  TImportFile,
  TRecordAction,
} from '@/core/types'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { AxiosError } from 'axios'
import {
  type TLead,
  TLeadMassiveEdit,
  type TLeads,
  moduleServiceHandlers,
  leadMassiveEditHandlers,
  moduleExportHandler,
  serviceConfig,
  validatePhoneService,
} from '../../..'

const { model } = serviceConfig

export const useModuleQuery = (options: BaseServiceOptions<Pagination>) => {
  const { currentPage, handlers, enableQuery, filters, modelId } = options
  const { errorHttpNotification } = useNotification()
  const { handleFormatDate } = useDate()

  const serviceHandlers = useServiceHandlers<TLead>({
    model,
    currentPage: currentPage ?? 1,
    filterParams: filters?.loadRecords ?? '',
  })

  const deleteRecord = useMutation<void, AxiosError, TLead>(
    (payload) => moduleServiceHandlers.deleteRecord(payload),
    {
      onSuccess: (_, lead) => serviceHandlers.deleteRecord(lead),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const deleteRecords = useMutation<void, AxiosError, Array<number>>(
    (payload) => moduleServiceHandlers.deleteRecords({ ids: payload }),
    {
      onSuccess: (_, ids) => serviceHandlers.deleteRecords(ids),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const editRecord = useMutation<TLead, AxiosError, TLead>(
    (payload: TLead) => moduleServiceHandlers.editRecord(payload, payload.id),
    {
      onSuccess: (payload) => serviceHandlers.updateRecord(payload),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const addRecord = useMutation<TLead, AxiosError, TLead>(
    (payload: TLead) => moduleServiceHandlers.addRecord(payload),
    {
      onSuccess: (payload) => serviceHandlers.addRecord(payload),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const actionRecord = useMutation<TLead, AxiosError, TRecordAction>(
    (payload: TRecordAction) => moduleServiceHandlers.actionRecord(payload),
    {
      onSuccess: (payload) => serviceHandlers.updateRecord(payload),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const importRecords = useMutation<void, AxiosError, TImportFile>(
    (payload: TImportFile) => {
      const formData = new FormData()
      formData.append('importFile', payload.file)
      return moduleServiceHandlers.importRecords(formData)
    },
    {
      onSuccess: () => serviceHandlers.invalidateQueries(),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const massiveEditRecords = useMutation<void, AxiosError, TLeadMassiveEdit>(
    (payload: TLeadMassiveEdit) => {
      return leadMassiveEditHandlers.massiveEditRecords(
        { dynamicFilters: filters?.loadRecords, page: currentPage },
        payload,
      )
    },
    {
      onSuccess: () => loadRecords.refetch(),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const loadRecords = useQuery<TLeads, AxiosError>(
    [model, currentPage, filters?.loadRecords],
    () =>
      moduleServiceHandlers.loadRecords({
        dynamicFilters: filters?.loadRecords,
        page: currentPage,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.loadRecords,
      onError: (error) => errorHttpNotification(error),
      onSuccess: (data) => {
        if (data.meta) {
          handlers?.onLoadRecords?.(data.meta ?? defaultPagination)
        }
      },
    },
  )

  const loadRecord = useQuery<TLead, AxiosError>(
    [model, modelId],
    ({ queryKey }) =>
      moduleServiceHandlers.loadRecord({
        id: queryKey[1] as number,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.loadRecord,
      onError: (error) => errorHttpNotification(error),
      onSuccess: (payload) => {
        options.handlers?.onLoadRecord?.(payload)
      },
      select: (payload) => ({
        ...payload,
        advisor_id: payload.advisor?.id,
        status_id: payload.status?.id,
        social_network_id: payload.social_network?.id,
        origin_id: payload.origin?.id,
        project_ids: [...(payload.projects ?? [])]?.map(
          (project) => project.id,
        ),
        lot_ids: [...(payload.lots ?? [])]?.map((lot) => lot.id),
        created_at: handleFormatDate(payload.created_at ?? '', 'MM/dd/yy'),
        organic_id: payload.organic?.id,
      }),
    },
  )

  const validatePhone = useMutation<boolean, AxiosError, { phone: string }>(
    (payload: { phone: string }) => validatePhoneService.validatePhone(payload),
    {
      onError: (error) => errorHttpNotification(error),
    },
  )

  const exportRecords = async () =>
    await moduleExportHandler.exportRecords({
      dynamicFilters: filters?.loadRecords,
    })

  return {
    validatePhone,
    loadRecords,
    addRecord,
    editRecord,
    deleteRecords,
    deleteRecord,
    importRecords,
    actionRecord,
    loadRecord,
    massiveEditRecords,
    exportRecords,
  }
}
